// BUILD-042 — My Team: roster of direct reports only (requirements AZ-4), rebuilt to the
// Owner-approved visual. Authority:
// docs/superpowers/decisions/2026-10-06-build-042-approved-my-team-layout.md
//
// Replaces the prior sparse two-row list AND the separate routed TeamMemberDetail.tsx page with
// a single compact roster of expandable cards — one person expanded at a time, breadcrumb
// reflects HR / My Team / <person> while expanded, collapsing back to HR / My Team never
// re-fetches the already-loaded roster (decision: "preserving loaded authorized roster and
// avoiding redundant fetches"). Expand/collapse is in-page state (openId), not a route
// transition, which is what makes that possible — the URL still updates via replace-navigation
// so a direct link to /hr/team/:employeeId still opens that person, but navigating within this
// mounted page never remounts it.
//
// GET /hr/team only returns { employeeId, name, currentTerm: { title, departmentId } } — no
// employment status, manager, or hire date (requirements AZ-9's deliberately thin team
// projection). GET /hr/team/{employeeId} (handleGetTeamMember, same hr.team.view capability —
// NOT an HR-admin-only endpoint, so prefetching it is within the decision's "do not fetch
// HR-admin-only endpoints" boundary) carries the real employment status/manager/hire-date/term
// history this screen's cards and Work details tab need. Prefetched for every direct report in
// parallel once, right after the roster loads, and cached — so expanding a card never issues a
// second fetch, and the collapsed card can show a REAL status badge instead of an invented one.
//
// Five detail destinations per the decision: Work details (REAL data, read-only); Reviews &
// notes / Tasks & time / Training & property / Documents (all honestly Planned — no backend,
// no fabricated counts, no fake saves — matching this app's existing Planned convention used
// throughout the rest of the unified workspace rather than inventing a second "proposed action"
// demo-button pattern just for this screen).
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMyTeam, getTeamMember, type HrTeamMemberResponse } from '../../api/client';
import { useDepartmentNames } from './useDepartmentNames';
import { Avatar } from './workspace/Avatar';
import { Breadcrumbs } from './workspace/Breadcrumbs';
import { splitTermHistory } from './termHistory';
import { formatLocalDate } from './hrFormat';

interface RosterEntry {
  employeeId: string;
  name: string | null;
  currentTerm: { title: string | null; departmentId: string | null } | null;
}

type DetailState = { kind: 'loading' } | { kind: 'ready'; record: HrTeamMemberResponse } | { kind: 'error'; message: string };

type TabKey = 'work' | 'reviews' | 'tasks' | 'training' | 'documents';
const TABS: { key: TabKey; label: string; planned: boolean }[] = [
  { key: 'work', label: 'Work details', planned: false },
  { key: 'reviews', label: 'Reviews & notes', planned: true },
  { key: 'tasks', label: 'Tasks & time', planned: true },
  { key: 'training', label: 'Training & property', planned: true },
  { key: 'documents', label: 'Documents', planned: true },
];

function statusTone(status: string | undefined | null): string {
  if (status === 'active') return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'leave_of_absence' || status === 'pre_start') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-500 border-gray-200';
}
function statusLabel(status: string | undefined | null): string {
  if (!status) return 'Status not recorded';
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

function PlannedPanel({ title }: { title: string }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <span className="text-[11px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">Planned</span>
      </div>
      <p className="text-sm text-gray-500 max-w-lg">
        {title} is part of the approved manager workspace design but does not have a real backend in this increment. Being this Employee's manager does not, by itself, change what you are authorized to see — the same capability/scope rules that govern Work details below will govern this once it is built.
      </p>
    </div>
  );
}

function WorkDetailsPanel({ record }: { record: HrTeamMemberResponse }) {
  const { nameOf } = useDepartmentNames();
  const { current } = splitTermHistory(record.termHistory);
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Current employment</h3>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div><dt className="text-xs text-gray-500 mb-0.5">Job title</dt><dd className="text-[13px] font-medium text-gray-900">{current?.title ?? 'Not recorded'}</dd></div>
        <div><dt className="text-xs text-gray-500 mb-0.5">Department</dt><dd className="text-[13px] font-medium text-gray-900">{current?.departmentId ? (nameOf(current.departmentId) ?? '…') : 'Not recorded'}</dd></div>
        <div><dt className="text-xs text-gray-500 mb-0.5">Reports to</dt><dd className="text-[13px] font-medium text-gray-900">{record.managerName ?? record.managerEmployeeId ?? 'Not recorded'}</dd></div>
        <div><dt className="text-xs text-gray-500 mb-0.5">Employment status</dt><dd className="text-[13px] font-medium text-gray-900">{statusLabel(record.period?.employmentStatus)}</dd></div>
        {/* October 10 22:28 addendum: hire date (and the term's effective date) were previously
            rendered as the raw stored YYYY-MM-DD string with no formatting at all — now
            MM/DD/YYYY via the same date-only-safe formatter used elsewhere in HR. The stored
            value itself is unchanged; this is display only. */}
        <div><dt className="text-xs text-gray-500 mb-0.5">Hire date</dt><dd className="text-[13px] font-medium text-gray-900">{record.period?.hireDate ? formatLocalDate(record.period.hireDate) : 'Not recorded'}</dd></div>
        <div><dt className="text-xs text-gray-500 mb-0.5">Current terms effective</dt><dd className="text-[13px] font-medium text-gray-900">{current?.effectiveDate ? formatLocalDate(current.effectiveDate) : 'Not recorded'}</dd></div>
      </dl>
      <div className="flex items-start gap-2 text-xs text-gray-400 mt-5 pt-3 border-t border-gray-100">
        <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
        <span>Read-only manager view. Contact HR if employment information needs correction.</span>
      </div>
    </div>
  );
}

export function MyTeamTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const { nameOf } = useDepartmentNames();
  const [reports, setReports] = useState<RosterEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, DetailState>>({});
  const [activeTab, setActiveTab] = useState<TabKey>('work');

  // The expanded person's id lives in the URL (/hr/team/:employeeId) via replace-navigation, not
  // component state alone — a direct link to a specific report still opens them, and this
  // component is mounted at path="/team/*" in HrPage.tsx (one Route, matching both "/team" and
  // "/team/:id") specifically so toggling never remounts this page or re-fetches the roster.
  const openId = location.pathname.replace(/^\/hr\/team\/?/, '') || null;

  useEffect(() => {
    getMyTeam()
      .then((data) => {
        setReports(data.reports);
        // Prefetch every direct report's detail in parallel, once — see this file's header for
        // why this is within the decision's authorized-endpoint boundary. Small list (direct
        // reports only), so this is not a scaling concern.
        data.reports.forEach((r: RosterEntry) => {
          setDetails((prev) => ({ ...prev, [r.employeeId]: { kind: 'loading' } }));
          getTeamMember(r.employeeId)
            .then((record) => setDetails((prev) => ({ ...prev, [r.employeeId]: { kind: 'ready', record } })))
            .catch((e: any) => setDetails((prev) => ({ ...prev, [r.employeeId]: { kind: 'error', message: e.message || 'Could not load this team member.' } })));
        });
      })
      .catch((e) => setError(e.message || 'Could not load your team.'));
  }, []);

  function toggle(employeeId: string) {
    setActiveTab('work');
    navigate(openId === employeeId ? '/hr/team' : `/hr/team/${employeeId}`, { replace: true });
  }

  if (error) return <div className="text-red-700">{error}</div>;
  if (!reports) return <div className="text-gray-500">Loading…</div>;

  const openPerson = openId ? reports.find((r) => r.employeeId === openId) : null;

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: 'HR', to: '/hr' },
          openPerson
            ? { label: 'My Team', onClick: () => navigate('/hr/team', { replace: true }) }
            : { label: 'My Team' },
          ...(openPerson ? [{ label: openPerson.name ?? openPerson.employeeId }] : []),
        ]}
      />

      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900 leading-tight">My Team</h1>
        <p className="text-sm text-gray-500 mt-1">Your direct reports, their roles and work information.</p>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-gray-900">Direct reports</h2>
        <span className="text-xs text-gray-400 border border-gray-200 rounded-full px-2 py-0.5 bg-white">
          {reports.length} {reports.length === 1 ? 'person' : 'people'}
        </span>
      </div>

      {reports.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-500">You have no direct reports.</div>
      ) : (
        reports.map((r) => {
          const name = r.name ?? r.employeeId;
          const isOpen = openId === r.employeeId;
          const detail = details[r.employeeId];
          return (
            <article
              key={r.employeeId}
              className={`bg-white rounded-[10px] border shadow-sm mb-3 overflow-hidden ${isOpen ? 'border-[#4AABDE]' : 'border-gray-200'}`}
            >
              <button
                type="button"
                onClick={() => toggle(r.employeeId)}
                aria-expanded={isOpen}
                className={`w-full flex items-center gap-3 text-left p-[18px] transition-colors ${isOpen ? 'bg-[#EDF5FC]' : ''}`}
              >
                <Avatar name={name} colorKey={r.employeeId} sizeClass="w-12 h-12" textClass="text-base" />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-gray-900 truncate">{name}</div>
                  <div className="text-[13px] text-gray-500 mt-0.5 truncate">{r.currentTerm?.title ?? 'No title on file'}</div>
                  {r.currentTerm?.departmentId && nameOf(r.currentTerm.departmentId) && <div className="text-xs text-gray-400 mt-0.5 truncate">{nameOf(r.currentTerm.departmentId)}</div>}
                </div>
                {detail?.kind === 'ready' && (
                  <span className={`text-[11px] font-medium rounded-full px-2.5 py-1 border flex-shrink-0 ${statusTone(detail.record.period?.employmentStatus)}`}>
                    {statusLabel(detail.record.period?.employmentStatus)}
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-xs text-[#4AABDE] flex-shrink-0">
                  <span>{isOpen ? 'Close details' : 'View details'}</span>
                  <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                  </svg>
                </span>
              </button>

              {isOpen && (
                <div className="px-[19px] pb-[19px] pt-4 border-t border-gray-100">
                  <div role="tablist" aria-label={`${name} detail sections`} className="flex gap-5 border-b border-gray-100 mb-4 flex-wrap">
                    {TABS.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`flex items-center gap-1.5 text-xs pb-2.5 border-b-2 -mb-px ${
                          activeTab === t.key ? 'text-[#4AABDE] border-[#4AABDE] font-semibold' : 'text-gray-500 border-transparent'
                        }`}
                      >
                        {t.label}
                        {t.planned && <span className="text-[10px] text-gray-400 border border-gray-200 rounded px-1 py-0.5 font-normal">Planned</span>}
                      </button>
                    ))}
                  </div>

                  {activeTab === 'work' && (
                    detail?.kind === 'ready' ? <WorkDetailsPanel record={detail.record} />
                    : detail?.kind === 'error' ? <div className="text-sm text-red-700">{detail.message}</div>
                    : <div className="text-sm text-gray-400">Loading…</div>
                  )}
                  {activeTab === 'reviews' && <PlannedPanel title="Reviews & notes" />}
                  {activeTab === 'tasks' && <PlannedPanel title="Tasks & time" />}
                  {activeTab === 'training' && <PlannedPanel title="Training & property" />}
                  {activeTab === 'documents' && <PlannedPanel title="Documents" />}
                </div>
              )}
            </article>
          );
        })
      )}

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">What can I see here?</summary>
        <p className="mt-2 max-w-lg">
          Your team view contains only information you are authorized to see for your direct reports. Personal contacts, emergency contacts, pay, and confidential HR records are not included here. Additional HR permissions are separate from being someone's manager.
        </p>
      </details>
    </div>
  );
}
